import * as THREE from 'three';
import { Track, Rng } from '@voidline/shared';
import { EnvironmentHandle } from './types.js';
import type { GraphicsPreset } from '../../persist.js';
import { glowSprite, duneTexture } from '../proctex.js';
import { sampleRibbon, clearOfRibbon } from './placement.js';

/**
 * THE DUNEWORKS — a desert world littered with machines a million years dead.
 * Colossal ring segments and slabs lie half-buried in rippled sand under twin
 * moons; dust drifts on the wind and the ruins keep a faint, slow heartbeat.
 */
export function buildDuneworks(scene: THREE.Scene, track: Track, quality: GraphicsPreset): EnvironmentHandle {
  const disposables: { dispose(): void }[] = [];
  const objects: THREE.Object3D[] = [];
  const rng = new Rng('duneworks');

  const ribbon = sampleRibbon(track, 80);
  const { cx, cy, cz } = ribbon;
  const groundY = ribbon.minY - 38;

  // ── sky: dusty dusk, violet zenith over an amber horizon ──
  {
    const geo = new THREE.SphereGeometry(3800, 24, 16);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {},
      vertexShader: `
        varying vec3 vDir;
        void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
      `,
      fragmentShader: `
        varying vec3 vDir;
        void main(){
          float h = vDir.y * 0.5 + 0.5;
          vec3 zenith = vec3(0.055, 0.030, 0.085);
          vec3 horizon = vec3(0.360, 0.190, 0.075);
          vec3 col = mix(horizon, zenith, pow(h, 0.65));
          // suspended dust band above the horizon
          col += vec3(0.20, 0.10, 0.03) * exp(-abs(vDir.y - 0.05) * 9.0);
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(geo, mat);
    scene.add(sky);
    objects.push(sky);
    disposables.push(geo, mat);
  }

  // ── faint stars through the dust ──
  {
    const count = quality === 'performance' ? 500 : 1100;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const a = rng.next() * Math.PI * 2;
      const z = rng.range(0.15, 1);
      const r = Math.sqrt(Math.max(0, 1 - z * z));
      pos[i * 3] = Math.cos(a) * r * 3500;
      pos[i * 3 + 1] = z * 3500;
      pos[i * 3 + 2] = Math.sin(a) * r * 3500;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xffeedd,
      size: 1.8,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });
    const stars = new THREE.Points(geo, mat);
    scene.add(stars);
    objects.push(stars);
    disposables.push(geo, mat);
  }

  // ── twin moons + halos ──
  {
    const glowTex = glowSprite();
    disposables.push(glowTex);
    const moons: [number, number, number, number][] = [
      // [radius, azimuth, elevation, color]
      [150, 0.7, 0.42, 0xd8c8b8],
      [90, 1.15, 0.30, 0xb09480],
    ];
    for (const [radius, az, el, color] of moons) {
      const dist = 3000;
      const x = cx + Math.cos(az) * Math.cos(el) * dist;
      const y = cy + Math.sin(el) * dist;
      const z = cz + Math.sin(az) * Math.cos(el) * dist;
      const geo = new THREE.SphereGeometry(radius, 24, 18);
      const mat = new THREE.MeshStandardMaterial({ color, roughness: 1, metalness: 0, fog: false });
      const moon = new THREE.Mesh(geo, mat);
      moon.position.set(x, y, z);
      const haloGeo = new THREE.PlaneGeometry(1, 1);
      const haloMat = new THREE.MeshBasicMaterial({
        map: glowTex,
        transparent: true,
        opacity: 0.35,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        color,
        fog: false,
      });
      const halo = new THREE.Mesh(haloGeo, haloMat);
      halo.position.set(x, y, z);
      halo.scale.setScalar(radius * 5);
      halo.lookAt(cx, cy, cz);
      scene.add(moon, halo);
      objects.push(moon, halo);
      disposables.push(geo, mat, haloGeo, haloMat);
    }
  }

  // ── lights: low amber sun, dusty bounce ──
  {
    const hemi = new THREE.HemisphereLight(0x9a6c48, 0x241407, 0.85);
    const sun = new THREE.DirectionalLight(0xffce90, 1.25);
    sun.position.set(cx - 800, cy + 350, cz + 400);
    const moonFill = new THREE.DirectionalLight(0xb0a0d0, 0.18);
    moonFill.position.set(cx + 700, cy + 600, cz - 300);
    scene.add(hemi, sun, moonFill);
    objects.push(hemi, sun, moonFill);
  }

  // ── sand floor ──
  {
    const tex = duneTexture(13);
    tex.repeat.set(26, 26);
    disposables.push(tex);
    const geo = new THREE.PlaneGeometry(8000, 8000);
    const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 1, metalness: 0 });
    const floor = new THREE.Mesh(geo, mat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(cx, groundY, cz);
    scene.add(floor);
    objects.push(floor);
    disposables.push(geo, mat);
  }

  // ── the dead machines: half-buried ring arcs and tilted slabs ──
  const machineMat = new THREE.MeshStandardMaterial({
    color: 0x2e2a20,
    emissive: 0x52e89a,
    emissiveIntensity: 0.14,
    roughness: 0.6,
    metalness: 0.75,
  });
  disposables.push(machineMat);
  {
    // ring arcs: torus centers sunk below the sand so only the crown shows
    const count = quality === 'performance' ? 7 : 14;
    const geo = new THREE.TorusGeometry(1, 0.06, 10, 48);
    const mesh = new THREE.InstancedMesh(geo, machineMat, count);
    const dummy = new THREE.Object3D();
    let placed = 0;
    let guard = 0;
    while (placed < count && guard++ < count * 40) {
      const scale = rng.range(150, 420);
      const x = cx + rng.range(-1900, 1900);
      const z = cz + rng.range(-1900, 1900);
      if (!clearOfRibbon(ribbon, x, z, scale + 100)) continue;
      dummy.position.set(x, groundY - scale * rng.range(0.25, 0.55), z);
      dummy.scale.setScalar(scale);
      dummy.rotation.set(rng.range(-0.2, 0.2), rng.range(0, Math.PI), rng.range(-0.15, 0.15));
      dummy.updateMatrix();
      mesh.setMatrixAt(placed, dummy.matrix);
      placed++;
    }
    mesh.count = placed;
    scene.add(mesh);
    objects.push(mesh);
    disposables.push(geo);
  }
  {
    // slabs: broken plates jutting out of the dunes at angles
    const count = quality === 'performance' ? 16 : 34;
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const mesh = new THREE.InstancedMesh(geo, machineMat, count);
    const dummy = new THREE.Object3D();
    let placed = 0;
    let guard = 0;
    while (placed < count && guard++ < count * 40) {
      const long = rng.range(90, 260);
      const x = cx + rng.range(-1700, 1700);
      const z = cz + rng.range(-1700, 1700);
      if (!clearOfRibbon(ribbon, x, z, long + 90)) continue;
      dummy.position.set(x, groundY + rng.range(-10, 30), z);
      dummy.scale.set(long, rng.range(50, 160), rng.range(12, 30));
      dummy.rotation.set(rng.range(-0.5, 0.5), rng.range(0, Math.PI), rng.range(-0.6, 0.6));
      dummy.updateMatrix();
      mesh.setMatrixAt(placed, dummy.matrix);
      placed++;
    }
    mesh.count = placed;
    scene.add(mesh);
    objects.push(mesh);
    disposables.push(geo);
  }

  // ── wind-blown dust around the camera ──
  const dustCount = quality === 'performance' ? 200 : 460;
  const dust = (() => {
    const tex = glowSprite();
    disposables.push(tex);
    const geo = new THREE.PlaneGeometry(0.5, 0.5);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity: 0.16,
      depthWrite: false,
      color: 0xd8a868,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, dustCount);
    mesh.frustumCulled = false;
    disposables.push(geo, mat);
    const offsets: THREE.Vector3[] = [];
    const speeds: number[] = [];
    for (let i = 0; i < dustCount; i++) {
      offsets.push(new THREE.Vector3(rng.range(-42, 42), rng.range(-18, 22), rng.range(-42, 42)));
      speeds.push(rng.range(9, 20));
    }
    scene.add(mesh);
    objects.push(mesh);
    return { mesh, offsets, speeds };
  })();
  const dustDummy = new THREE.Object3D();

  let t = 0;
  return {
    update(dt, camPos, _time) {
      t += dt;
      // the ruins' slow heartbeat
      machineMat.emissiveIntensity = 0.1 + 0.07 * (0.5 + 0.5 * Math.sin(t * 0.45));
      // dust streams sideways on the wind, wraps in a camera-local box
      for (let i = 0; i < dust.offsets.length; i++) {
        const o = dust.offsets[i];
        o.x += dt * dust.speeds[i];
        if (o.x > 42) o.x -= 84;
        const lift = Math.sin(t * 0.9 + i * 0.77) * 1.6;
        dustDummy.position.set(camPos.x + o.x, camPos.y + o.y + lift, camPos.z + o.z);
        dustDummy.rotation.set(0, ((i * 29) % 100) * 0.0628, 0);
        dustDummy.updateMatrix();
        dust.mesh.setMatrixAt(i, dustDummy.matrix);
      }
      dust.mesh.instanceMatrix.needsUpdate = true;
    },
    dispose() {
      for (const o of objects) o.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}
